import React from 'react';

export default function HistoryPanel({ history, setHistory }) {
  const handleClearHistory = () => {
    setHistory([]);
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-green-100 text-green-700 border-green-200',
      POST: 'bg-blue-100 text-blue-700 border-blue-200',
      PUT: 'bg-orange-100 text-orange-700 border-orange-200',
      PATCH: 'bg-purple-100 text-purple-700 border-purple-200',
      DELETE: 'bg-red-100 text-red-700 border-red-200',
    };
    return colors[method] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600';
    if (status >= 300 && status < 400) return 'text-blue-600';
    if (status >= 400 && status < 500) return 'text-orange-600';
    if (status >= 500) return 'text-red-600';
    return 'text-red-500';
  };

  const getStatusIcon = (status) => {
    if (status >= 200 && status < 300) {
      return (
        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    );
  };

  return (
    <div className="w-full bg-white h-screen overflow-hidden border-l border-gray-200 flex flex-col shadow-lg">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[#fdb814]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-base font-bold text-gray-800">Request History</h2>
          </div>
          {history.length > 0 && (
            <button
              className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200 group"
              onClick={handleClearHistory}
              title="Clear All History"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="px-2 py-1 bg-gray-100 rounded-full font-medium">{history.length} {history.length === 1 ? 'request' : 'requests'}</span>
        </div>
      </div>

      {/* History List */}
      <div className="flex-1 overflow-y-auto pb-4">
        {history.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 mb-1">No requests yet</p>
            <p className="text-xs text-gray-400">Your API request history will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((entry, index) => (
              <div
                key={index}
                className="px-4 py-3 hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
              >
                {/* Method & Time */}
                <div className="flex justify-between items-center mb-2">
                  <span className={`px-2 py-0.5 text-xs font-bold rounded border ${getMethodColor(entry.method)}`}>
                    {entry.method}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">
                    {new Date(entry.timestamp).toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </span>
                </div>

                {/* URL */}
                <p className="text-xs text-gray-700 truncate mb-2 font-medium group-hover:text-[#fdb814] transition-colors" title={entry.url}>
                  {entry.url}
                </p>

                {/* Status */}
                <div className="flex items-center gap-2">
                  {getStatusIcon(entry.status)}
                  <span className={`text-xs font-semibold ${getStatusColor(entry.status)}`}>
                    {entry.status}
                  </span>
                  {entry.status >= 200 && entry.status < 300 && (
                    <span className="text-xs text-gray-400">• Success</span>
                  )}
                  {(typeof entry.status === 'string' && entry.status.includes('Error')) && (
                    <span className="text-xs text-red-400">• Failed</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {history.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex justify-between items-center text-xs">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-gray-600">
                {history.filter(h => h.status >= 200 && h.status < 300).length} success
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-gray-600">
                {history.filter(h => h.status >= 400 || typeof h.status === 'string').length} failed
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}